import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, UserX } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { GlassCard } from "@/components/GlassCard";
import { useLanguage } from "@/i18n/LanguageContext";
import { listBlocksApi, unblockUserApi } from "@/lib/api";

/**
 * Phase 10 (Google Play UGC compliance): manage blocked users from the
 * Profile screen. Lists blocks created by the current profile with an
 * Unblock action per row.
 */
export const BlockedUsersCard = ({ profileId }) => {
  const { t } = useLanguage();
  const [blocks, setBlocks] = useState(null); // null = loading
  const [error, setError] = useState(false);
  const [unblockingId, setUnblockingId] = useState(null);

  const load = useCallback(async () => {
    setError(false);
    try {
      setBlocks(await listBlocksApi(profileId));
    } catch {
      setError(true);
      setBlocks([]);
    }
  }, [profileId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleUnblock = async (blockedId) => {
    setUnblockingId(blockedId);
    try {
      await unblockUserApi(profileId, blockedId);
      setBlocks((prev) => prev.filter((b) => b.profile_id !== blockedId));
      toast.success(t("block.unblockedToast"));
    } catch {
      toast.error(t("block.unblockError"));
    } finally {
      setUnblockingId(null);
    }
  };

  return (
    <GlassCard className="p-4" data-testid="blocked-users-card">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
          <UserX size={16} className="text-cyan-300/80" />
        </div>
        <div>
          <p className="text-sm font-medium">{t("block.sectionTitle")}</p>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">
            {t("block.sectionDesc")}
          </p>
        </div>
      </div>

      <div className="mt-3">
        {blocks === null ? (
          <Skeleton className="h-10 rounded-xl bg-white/10" />
        ) : error ? (
          <p data-testid="blocked-users-error" className="text-xs text-red-300/90">
            {t("block.loadError")}
          </p>
        ) : blocks.length === 0 ? (
          <p
            data-testid="blocked-users-empty"
            className="text-xs text-[hsl(var(--muted-foreground))]"
          >
            {t("block.empty")}
          </p>
        ) : (
          <ul className="space-y-2" data-testid="blocked-users-list">
            {blocks.map((b) => (
              <li
                key={b.profile_id}
                className="flex items-center justify-between gap-3 rounded-xl bg-white/5 border border-white/10 px-3 py-2"
              >
                <span className="text-sm truncate">{b.name}</span>
                <button
                  data-testid={`unblock-button-${b.profile_id}`}
                  onClick={() => handleUnblock(b.profile_id)}
                  disabled={unblockingId === b.profile_id}
                  className="shrink-0 flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold border bg-white/5 border-white/10 text-white/80 hover:bg-white/10 transition-colors duration-200 active:scale-[0.96] disabled:opacity-60"
                >
                  {unblockingId === b.profile_id ? (
                    <Loader2 className="animate-spin" size={12} />
                  ) : (
                    t("block.unblock")
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </GlassCard>
  );
};
